import { useQuery } from '@tanstack/react-query';
import { PublicAPI } from '../utils/PublicAPI';
import { API } from '../utils/ZiphyAPI';

const STORAGE_KEY = 'document_types.full_list';
const EXPIRY_HOURS = 24;

type ApiResponse = Record<string, string>;

function getLocalStorageData(): { data: ApiResponse; expiry: number } | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed.data && parsed.expiry ? parsed : null;
    } catch {
        return null;
    }
}

function setLocalStorageData(data:ApiResponse) {
    const expiry = Date.now() + EXPIRY_HOURS * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, expiry }));
}

export function useDocumentTypeQuery(usePublicAPI: boolean) {
    return useQuery({
        queryKey: ['document_types.full_list'],
        queryFn: async () => {
            const local = getLocalStorageData();
            const isExpired = !local || local.expiry < Date.now();

            if (local && local.data && !isExpired) {
                return local.data;
            }

            const freshData = usePublicAPI
                ? await PublicAPI.getDocumentTypes()
                : await API.getDocumentTypes();
            if (!freshData) {
                throw new Error('Failed to retrieve document data');
            }

            setLocalStorageData(freshData);
            return freshData;
        },
        staleTime:60_000
    });
}